'use client';

import { Cta } from '@/components/cms/Card/components/Cta';
import { Icon, LinkButton } from '@/components/ui';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { EVENT_NAME } from '@/lib/gtm/const';
import { resolvePromotionFromCms } from '@/lib/gtm/promotion';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import styles from '../HeroSlider.module.scss';
import type { SlideContentProps } from '../HeroSlider.types';
import { parseLineBreaks } from '../HeroSlider.utils';

export const SlideContent = ({ slide, slideIndex, isActive, testId }: SlideContentProps) => {
  const t = useTranslations('product.cta');
  const { sendPromotionEvent } = useGoogleAnalytics();
  const { heading, subheading, subtitleAbove } = slide;
  const promotionTracking = resolvePromotionFromCms(slide, slideIndex);
  const ctaLabel = slide.sourceType === 'Page' ? t('discover') : slide.cta?.text;
  const ctaUrl =
    slide.sourceType === 'Page'
      ? slide.slug
        ? `/${slide.slug.replace(/^\//, '')}`
        : '#'
      : slide.cta?.url;
  const ctaVariant = slide.ctaVariant || null;

  const handlePromotionSelect = () => {
    sendPromotionEvent(EVENT_NAME.SELECT_PROMOTION, promotionTracking).catch(() => undefined);
  };

  return (
    <div
      className={clsx(styles.dynamicContent, isActive && styles.dynamicContentActive)}
      aria-hidden={!isActive}
      data-testid={testId('dynamic-content')}
    >
      {(subtitleAbove || subheading) && (
        <p className={styles.dynamicSubtitleAbove} data-testid={testId('dynamic-subtitle-above')}>
          {parseLineBreaks(subtitleAbove || subheading || '')}
        </p>
      )}

      {heading && (
        <h2 className={styles.dynamicHeading} data-testid={testId('dynamic-heading')}>
          {parseLineBreaks(heading)}
        </h2>
      )}

      {ctaLabel && ctaUrl && (
        <>
          {ctaVariant ? (
            <Cta
              urlText={ctaLabel}
              url={ctaUrl}
              urlType={ctaVariant}
              testIdPrefix={testId('dynamic') || undefined}
              promotionTracking={promotionTracking}
            />
          ) : (
            <LinkButton
              href={ctaUrl}
              variant="secondary"
              iconAfter={<Icon name="arrow-right" size={16} />}
              data-testid={testId('dynamic-cta')}
              onClick={handlePromotionSelect}
            >
              {ctaLabel}
            </LinkButton>
          )}
        </>
      )}
    </div>
  );
};
